import React, { useCallback, useEffect, useState } from 'react';
import { Task } from '../types/task';
import { taskService } from '../services/taskService';
import { useTenant } from '../context/TenantContext';
import PatientIssuesModal from './modals/PatientIssuesModal';
import { useTaskActions } from './tasks/useTaskActions';

interface Props {
  patientId: number;
}

const PatientIssuesBadge: React.FC<Props> = ({ patientId }) => {
  const { currentTenant } = useTenant();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const loadTasks = useCallback(() => {
    if (!currentTenant?.id) return;
    taskService.getPatientTasks(patientId, currentTenant.id)
      .then(setTasks)
      .catch(() => {}); // silent fail — badge just stays empty
  }, [patientId, currentTenant?.id]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Resolving an issue from its popup should update the badge behind it.
  const { openTask, dialogs: taskActionDialogs } = useTaskActions({ onChanged: loadTasks });

  if (tasks.length === 0) return <p className="text-sm font-medium text-gray-900">-</p>;

  const hasError = tasks.some(t => t.severity === 'ERROR');
  const chipClass = hasError
    ? 'border border-red-500 text-red-600 bg-red-50 rounded px-2 py-0.5 text-xs font-semibold cursor-pointer hover:bg-red-100'
    : 'border border-amber-500 text-amber-700 bg-amber-50 rounded px-2 py-0.5 text-xs font-semibold cursor-pointer hover:bg-amber-100';

  const label = hasError ? `Errors: ${tasks.length}` : `Warnings: ${tasks.length}`;

  return (
    <>
      <button className={chipClass} onClick={() => setModalOpen(true)}>
        {label}
      </button>
      <PatientIssuesModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        tasks={tasks}
        onOpenTask={(task) => {
          // The issues list sits at z-1400, above MUI dialogs — it has to close,
          // or it covers the action popup it just opened.
          setModalOpen(false);
          openTask(task);
        }}
      />
      {taskActionDialogs}
    </>
  );
};

export default PatientIssuesBadge;
